import { getTenant } from "./tenantApi";
import {
  TENANT_STATUS_ACTIVE,
  TENANT_STATUS_PENDING_APPROVAL,
  TENANT_STATUS_ONBOARDING,
  getTenantStatusFromClaims,
  getStoredTenantId,
  hasCompletedOnboarding,
  markOnboardingComplete,
  markOnboardingSubmitted,
  hasSubmittedForApproval,
  userKeyFromClaims,
} from "./onboarding";

function pendingAccessState(tenantId) {
  return {
    tenant: { tenant_id: tenantId, status: TENANT_STATUS_PENDING_APPROVAL },
    approved: false,
    pending: true,
    wizard: false,
  };
}

function normalizeTenant(tenant) {
  if (!tenant) return tenant;
  if (tenant.status === "pending" && tenant.submitted_for_approval_at) {
    return { ...tenant, status: TENANT_STATUS_PENDING_APPROVAL };
  }
  return tenant;
}

function isPendingApprovalStatus(status) {
  return status === TENANT_STATUS_PENDING_APPROVAL || status === "pending";
}

async function tenantStateFromSessionClaims() {
  try {
    const { getIdTokenClaims } = await import("./auth");
    const claims = await getIdTokenClaims();
    const status = getTenantStatusFromClaims(claims);
    const tenantId = claims["custom:tenant_id"];
    if (isPendingApprovalStatus(status) && tenantId && tenantId !== "pending") {
      return pendingAccessState(tenantId);
    }
    // JWT dice active: no empujar al wizard si myTenant falló
    if (status === TENANT_STATUS_ACTIVE && tenantId && tenantId !== "pending") {
      return {
        tenant: {
          tenant_id: tenantId,
          status: TENANT_STATUS_ACTIVE,
          submitted_for_approval_at: claims["custom:submitted_for_approval_at"] || true,
        },
        approved: true,
        pending: false,
        wizard: false,
      };
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** Fallback cuando myTenant falla pero el usuario ya envió solicitud (localStorage). */
async function tenantStateFromLocalStorage() {
  try {
    const { getIdTokenClaims } = await import("./auth");
    const claims = await getIdTokenClaims();
    const userKey = userKeyFromClaims(claims);
    if (!userKey || !hasSubmittedForApproval(userKey) || hasCompletedOnboarding(userKey)) {
      return null;
    }
    const tenantId =
      (claims["custom:tenant_id"] && claims["custom:tenant_id"] !== "pending"
        ? claims["custom:tenant_id"]
        : null) || getStoredTenantId(userKey);
    if (!tenantId) return null;
    return pendingAccessState(tenantId);
  } catch {
    return null;
  }
}

/** Tenant aprobado — acceso completo a la app (legacy active sin submitted_at también). */
export function isTenantApprovedForApp(tenant) {
  const normalized = normalizeTenant(tenant);
  return normalized?.status === TENANT_STATUS_ACTIVE;
}

export function isTenantPendingApproval(tenant) {
  const normalized = normalizeTenant(tenant);
  return normalized?.status === TENANT_STATUS_PENDING_APPROVAL;
}

/** Solo status `onboarding` queda en el wizard. Active (con o sin solicitud) → app. */
export function isTenantInOnboardingWizard(tenant) {
  const normalized = normalizeTenant(tenant);
  if (!normalized) return false;
  if (isTenantPendingApproval(normalized)) return false;
  if (normalized.status === TENANT_STATUS_ACTIVE) return false;
  return normalized.status === TENANT_STATUS_ONBOARDING;
}

export async function fetchTenantAccessState() {
  try {
    const tenant = normalizeTenant(await getTenant());
    return {
      tenant,
      approved: isTenantApprovedForApp(tenant),
      pending: isTenantPendingApproval(tenant),
      wizard: isTenantInOnboardingWizard(tenant),
    };
  } catch {
    const jwtFallback = await tenantStateFromSessionClaims();
    if (jwtFallback) return jwtFallback;
    const localFallback = await tenantStateFromLocalStorage();
    if (localFallback) return localFallback;
    return {
      tenant: null,
      approved: false,
      pending: false,
      wizard: false,
      error: true,
    };
  }
}

export function syncOnboardingFlagsFromTenant(tenant, userKey) {
  if (!tenant?.tenant_id || !userKey) return;

  if (isTenantApprovedForApp(tenant)) {
    markOnboardingComplete(userKey, tenant.tenant_id, tenant.name);
    return;
  }
  if (isTenantPendingApproval(tenant)) {
    markOnboardingSubmitted(userKey, tenant.tenant_id, tenant.name);
  }
}

export { hasSubmittedForApproval };
